"use client"
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { AlertTriangle, Loader2, RotateCcw, Trash2 } from 'lucide-react';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmText?: string;
  secondaryConfirmText?: string | undefined;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void | Promise<void>;
  onSecondaryConfirm?: (() => void | Promise<void>) | undefined;
  loading?: boolean;
  secondaryLoading?: boolean | undefined;
}

export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Yes, Continue',
  secondaryConfirmText,
  cancelText = 'Cancel',
  variant = 'warning',
  onConfirm,
  onSecondaryConfirm,
  loading = false,
  secondaryLoading = false
}: ConfirmDialogProps) {
  const handleConfirm = async () => {
    await onConfirm();
    onOpenChange(false);
  };

  const handleSecondaryConfirm = async () => {
    if (!onSecondaryConfirm) return;
    await onSecondaryConfirm();
    onOpenChange(false);
  };

  const variantStyles = {
    danger: {
      icon: Trash2,
      iconColor: 'text-red-600',
      iconBg: 'bg-red-100',
      confirmButton: 'bg-red-600 hover:bg-red-700 text-white',
      border: 'border-red-200'
    },
    warning: {
      icon: AlertTriangle,
      iconColor: 'text-yellow-600',
      iconBg: 'bg-yellow-100',
      confirmButton: 'bg-yellow-600 hover:bg-yellow-700 text-white',
      border: 'border-yellow-200'
    },
    info: {
      icon: RotateCcw,
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-100',
      confirmButton: 'bg-blue-600 hover:bg-blue-700 text-white',
      border: 'border-blue-200'
    }
  };

  const style = variantStyles[variant];
  const Icon = style.icon;

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] animate-in fade-in" />
        <AlertDialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-xl bg-white rounded-xl shadow-2xl z-[201] border border-gray-200 overflow-hidden">
          <div className="p-6">
            {/* Icon and Title */}
            <div className="flex items-start gap-4 mb-4">
              <div className={`flex-shrink-0 w-12 h-12 rounded-full ${style.iconBg} flex items-center justify-center`}>
                <Icon className={`h-6 w-6 ${style.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <AlertDialog.Title className="text-xl font-semibold text-gray-900 mb-2">
                  {title}
                </AlertDialog.Title>
                <AlertDialog.Description className="text-sm text-gray-600 leading-relaxed">
                  {description}
                </AlertDialog.Description>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 mt-6">
              <AlertDialog.Cancel asChild>
                <button
                  disabled={loading || secondaryLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {cancelText}
                </button>
              </AlertDialog.Cancel>
              {secondaryConfirmText && onSecondaryConfirm && (
                <button
                  type="button"
                  onClick={handleSecondaryConfirm}
                  disabled={loading || secondaryLoading}
                  className="px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-red-600 hover:bg-red-700 text-white"
                >
                  {secondaryLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    secondaryConfirmText
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading || secondaryLoading}
                className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${style.confirmButton}`}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  confirmText
                )}
              </button>
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

